import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { Plus, Minus, Trash2, Copy, Table as TableIcon, Bold as BoldIcon, Sigma } from 'lucide-react';

// ---------------------------------------------------------------------------
// Mini-spreadsheet — a self-contained TipTap node with live formulas.
// Cell raw values may be plain text/numbers or a formula beginning with "="
// (e.g. =SUM(A1:B2), =A1*1.18, =AVERAGE(A1:A5)). Values are computed live and
// the raw grid can be copied as TSV so formulas paste straight into Excel.
// ---------------------------------------------------------------------------

const DEFAULT_ROWS = 4;
const DEFAULT_COLS = 4;

function emptyCell() { return { v: '', b: false, bg: null, a: 'left' }; }
function makeGrid(rows, cols) {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => emptyCell()));
}
function colLabel(i) {
  let s = ''; i += 1;
  while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26); }
  return s;
}
function colToIndex(letters) {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

// --- Excel / Sheets paste → grid --------------------------------------------
// Excel, Google Sheets and web tables put an HTML <table> on the clipboard;
// plain-text spreadsheet copies come through as tab-separated values (TSV).
function cellFrom(v) { return { v: v == null ? '' : String(v), b: false, bg: null, a: 'left' }; }

function gridFromRows(rows) {
  const clean = (rows || []).filter((r) => Array.isArray(r));
  if (!clean.length) return null;
  const maxCols = clean.reduce((m, r) => Math.max(m, r.length), 0);
  if (maxCols === 0) return null;
  return clean.map((r) => {
    const out = r.map((v) => cellFrom(v));
    while (out.length < maxCols) out.push(emptyCell());
    return out;
  });
}

function parseHtmlTable(html) {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const table = doc.querySelector('table');
    if (!table) return null;
    const trs = Array.from(table.querySelectorAll('tr'));
    const rows = trs
      .map((tr) => Array.from(tr.querySelectorAll('th,td')).map((td) => (td.textContent || '').replace(/\s+/g, ' ').trim()))
      .filter((r) => r.length);
    return gridFromRows(rows);
  } catch { return null; }
}

function parseTsv(text) {
  if (!text || text.indexOf('\t') === -1) return null; // needs tabs to be tabular
  const body = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '');
  const rows = body.split('\n').map((ln) => ln.split('\t'));
  const maxCols = rows.reduce((m, r) => Math.max(m, r.length), 0);
  if (rows.length < 1 || maxCols < 2) return null;
  return gridFromRows(rows);
}

// --- Tiny formula engine ---------------------------------------------------
function computeValue(raw, grid, seen) {
  if (raw == null) return '';
  const s = String(raw);
  if (!s.startsWith('=')) {
    return s; // literal
  }
  try {
    return evalExpr(s.slice(1), grid, seen);
  } catch {
    return '#ERR';
  }
}

function cellNumber(ref, grid, seen) {
  const m = /^([A-Za-z]+)(\d+)$/.exec(ref);
  if (!m) throw new Error('ref');
  const c = colToIndex(m[1]);
  const r = parseInt(m[2], 10) - 1;
  if (r < 0 || c < 0 || r >= grid.length || c >= (grid[0] || []).length) return 0;
  const key = `${r},${c}`;
  if (seen.has(key)) throw new Error('cycle');
  const raw = (grid[r][c] || {}).v;
  if (raw == null || raw === '') return 0;
  const s2 = new Set(seen); s2.add(key);
  const val = computeValue(raw, grid, s2);
  const num = parseFloat(val);
  return isNaN(num) ? 0 : num;
}

function rangeValues(a, b, grid, seen) {
  const ma = /^([A-Za-z]+)(\d+)$/.exec(a); const mb = /^([A-Za-z]+)(\d+)$/.exec(b);
  if (!ma || !mb) throw new Error('range');
  const c1 = colToIndex(ma[1]); const r1 = parseInt(ma[2], 10) - 1;
  const c2 = colToIndex(mb[1]); const r2 = parseInt(mb[2], 10) - 1;
  const out = [];
  for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++) {
    for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) {
      out.push(cellNumber(`${colLabel(c)}${r + 1}`, grid, seen));
    }
  }
  return out;
}

const FUNCS = {
  SUM: (a) => a.reduce((x, y) => x + y, 0),
  AVERAGE: (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0),
  AVG: (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0),
  MIN: (a) => (a.length ? Math.min(...a) : 0),
  MAX: (a) => (a.length ? Math.max(...a) : 0),
  COUNT: (a) => a.length,
  PRODUCT: (a) => a.reduce((x, y) => x * y, 1),
};

// Functions offered by the toolbar formula palette.
const FX_LIST = [
  { fn: 'SUM', desc: 'Add a range' },
  { fn: 'AVERAGE', desc: 'Mean of a range' },
  { fn: 'MIN', desc: 'Smallest value' },
  { fn: 'MAX', desc: 'Largest value' },
  { fn: 'COUNT', desc: 'Count values' },
];

// Recursive-descent evaluator over a token stream.
function evalExpr(expr, grid, seen) {
  let i = 0;
  const src = expr;
  const peek = () => src[i];
  const skipWs = () => { while (i < src.length && /\s/.test(src[i])) i++; };

  function parseExpression() {
    let v = parseTerm();
    skipWs();
    while (peek() === '+' || peek() === '-') {
      const op = src[i++]; const r = parseTerm();
      v = op === '+' ? v + r : v - r; skipWs();
    }
    return v;
  }
  function parseTerm() {
    let v = parseFactor();
    skipWs();
    while (peek() === '*' || peek() === '/') {
      const op = src[i++]; const r = parseFactor();
      v = op === '*' ? v * r : v / r; skipWs();
    }
    return v;
  }
  function parseFactor() {
    skipWs();
    const ch = peek();
    if (ch === '(') { i++; const v = parseExpression(); skipWs(); if (peek() === ')') i++; return v; }
    if (ch === '-') { i++; return -parseFactor(); }
    if (ch === '+') { i++; return parseFactor(); }
    // number
    if (/[0-9.]/.test(ch)) {
      let num = '';
      while (i < src.length && /[0-9.]/.test(src[i])) num += src[i++];
      return parseFloat(num);
    }
    // identifier: function name or cell ref
    if (/[A-Za-z]/.test(ch)) {
      let id = '';
      while (i < src.length && /[A-Za-z]/.test(src[i])) id += src[i++];
      let digits = '';
      while (i < src.length && /[0-9]/.test(src[i])) digits += src[i++];
      skipWs();
      if (peek() === '(') {
        // function call
        i++; const args = [];
        skipWs();
        if (peek() !== ')') {
          do {
            skipWs();
            // detect range like A1:B2
            const start = i;
            let tok = '';
            while (i < src.length && /[A-Za-z0-9]/.test(src[i])) tok += src[i++];
            skipWs();
            if (peek() === ':') {
              i++; skipWs();
              let tok2 = '';
              while (i < src.length && /[A-Za-z0-9]/.test(src[i])) tok2 += src[i++];
              args.push(...rangeValues(tok, tok2, grid, seen));
            } else {
              // single token — could be a ref/number; re-evaluate as sub-expression
              i = start;
              args.push(parseExpression());
            }
            skipWs();
          } while (peek() === ',' && (i++, true));
        }
        skipWs(); if (peek() === ')') i++;
        const fn = FUNCS[id.toUpperCase()];
        if (!fn) throw new Error('fn');
        return fn(args);
      }
      // cell reference
      if (digits) return cellNumber(`${id}${digits}`, grid, seen);
      throw new Error('token');
    }
    throw new Error('parse');
  }

  const result = parseExpression();
  if (typeof result === 'number' && !isFinite(result)) return '#ERR';
  // Trim floating noise
  return typeof result === 'number' ? Math.round(result * 1e10) / 1e10 : result;
}

// --- React NodeView --------------------------------------------------------
function SpreadsheetView({ node, updateAttributes, editor, deleteNode }) {
  const initial = node.attrs.data && Array.isArray(node.attrs.data) && node.attrs.data.length
    ? node.attrs.data
    : makeGrid(DEFAULT_ROWS, DEFAULT_COLS);
  const [grid, setGrid] = useState(initial);
  const [active, setActive] = useState({ r: 0, c: 0 }); // selected cell
  const [editing, setEditing] = useState(null); // {r,c} currently text-editing
  const [copied, setCopied] = useState(false);
  const [fxOpen, setFxOpen] = useState(false); // formula palette dropdown
  const fxRef = useRef(null);
  const fxCursorRef = useRef(false); // place caret inside =FUNC() after insert
  const selectOnFocusRef = useRef(false); // select all when entering a cell via keyboard (Tab/Enter)
  const editInputRef = useRef(null); // ref to the live editing input (to commit before switching cells)
  const editable = editor?.isEditable !== false;

  const persist = useCallback((g) => { updateAttributes({ data: g }); }, [updateAttributes]);
  const commit = useCallback((g) => { setGrid(g); persist(g); }, [persist]);

  const rows = grid.length;
  const cols = (grid[0] || []).length;

  const display = useMemo(() => grid.map((row, r) => row.map((cell, c) => {
    const raw = cell?.v ?? '';
    if (String(raw).startsWith('=')) {
      const val = computeValue(raw, grid, new Set([`${r},${c}`]));
      return String(val);
    }
    return String(raw);
  })), [grid]);

  const setCellValue = (r, c, v) => {
    const g = grid.map((row) => row.map((cell) => ({ ...cell })));
    g[r][c].v = v;
    commit(g);
  };
  const patchCell = (r, c, patch) => {
    const g = grid.map((row) => row.map((cell) => ({ ...cell })));
    g[r][c] = { ...g[r][c], ...patch };
    commit(g);
  };

  const addRow = () => { const g = grid.map((row) => row.map((cell) => ({ ...cell }))); g.push(Array.from({ length: cols }, () => emptyCell())); commit(g); };
  const addCol = () => { const g = grid.map((row) => [...row.map((cell) => ({ ...cell })), emptyCell()]); commit(g); };
  const removeRow = () => { if (rows <= 1) return; const g = grid.slice(0, rows - 1).map((row) => row.map((cell) => ({ ...cell }))); commit(g); setActive((a) => ({ ...a, r: Math.min(a.r, g.length - 1) })); };
  const removeCol = () => { if (cols <= 1) return; const g = grid.map((row) => row.slice(0, cols - 1).map((cell) => ({ ...cell }))); commit(g); setActive((a) => ({ ...a, c: Math.min(a.c, cols - 2) })); };

  const toggleBold = () => { const { r, c } = active; patchCell(r, c, { b: !grid[r][c].b }); };
  const setBg = (bg) => { const { r, c } = active; patchCell(r, c, { bg }); };
  const setAlign = (a) => { const { r, c } = active; patchCell(r, c, { a }); };

  // Formula palette — insert =FUNC() into the active cell and start editing.
  const insertFormula = (fn) => {
    const { r, c } = active;
    fxCursorRef.current = true;
    setCellValue(r, c, `=${fn}()`);
    setEditing({ r, c });
    setFxOpen(false);
  };
  useEffect(() => {
    if (!fxOpen) return undefined;
    const h = (e) => { if (fxRef.current && !fxRef.current.contains(e.target)) setFxOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [fxOpen]);

  const copyForExcel = async () => {
    // TSV of RAW values → formulas paste as real formulas into Excel/Sheets.
    const tsv = grid.map((row) => row.map((cell) => (cell?.v ?? '')).join('\t')).join('\n');
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(tsv);
      } else {
        const ta = document.createElement('textarea'); ta.value = tsv; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
      }
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const BG_SWATCHES = [null, '#FBEFD3', '#E3F0E5', '#E1EAF6', '#F5E1EC', '#EDE6F5'];

  return (
    <NodeViewWrapper className="notes-sheet my-4" data-testid="notes-spreadsheet">
      {editable && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1" contentEditable={false} data-testid="notes-spreadsheet-toolbar">
          <span className="mr-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]"><TableIcon size={12} /> Spreadsheet</span>
          <button type="button" onClick={addRow} title="Add row" className="sheet-btn"><Plus size={12} /> Row</button>
          <button type="button" onClick={removeRow} title="Remove row" className="sheet-btn"><Minus size={12} /> Row</button>
          <button type="button" onClick={addCol} title="Add column" className="sheet-btn"><Plus size={12} /> Col</button>
          <button type="button" onClick={removeCol} title="Remove column" className="sheet-btn"><Minus size={12} /> Col</button>
          <span className="mx-1 h-4 w-px bg-[var(--n-border)]" />
          <button type="button" onClick={toggleBold} title="Bold cell" className={`sheet-btn ${grid[active.r]?.[active.c]?.b ? 'sheet-btn-on' : ''}`}><BoldIcon size={12} /></button>
          <button type="button" onClick={() => setAlign('left')} title="Align left" className="sheet-btn">L</button>
          <button type="button" onClick={() => setAlign('center')} title="Align center" className="sheet-btn">C</button>
          <button type="button" onClick={() => setAlign('right')} title="Align right" className="sheet-btn">R</button>
          <span className="mx-0.5 inline-flex items-center gap-1">
            {BG_SWATCHES.map((c, idx) => (
              <button key={idx} type="button" title={c ? 'Fill colour' : 'No fill'} onClick={() => setBg(c)}
                className="h-5 w-5 rounded ring-1 ring-inset ring-[var(--n-border)]"
                style={{ background: c || 'var(--n-surface)' }}>{!c && <span className="text-[9px] text-[var(--n-muted-2)]">∅</span>}</button>
            ))}
          </span>
          <span className="mx-1 h-4 w-px bg-[var(--n-border)]" />
          <div className="relative" ref={fxRef}>
            <button type="button" onClick={() => setFxOpen((o) => !o)} title="Insert formula" className={`sheet-btn ${fxOpen ? 'sheet-btn-on' : ''}`} data-testid="notes-spreadsheet-fx">
              <Sigma size={12} /> fx
            </button>
            {fxOpen && (
              <div className="absolute left-0 top-full z-[60] mt-1 w-56 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="notes-spreadsheet-fx-menu">
                {FX_LIST.map((f) => (
                  <button key={f.fn} type="button" onClick={() => insertFormula(f.fn)} data-testid={`notes-spreadsheet-fx-${f.fn.toLowerCase()}`}
                    className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left hover:bg-[var(--n-canvas-2)]">
                    <span className="font-mono text-[12px] font-semibold text-[var(--n-ink)]">={f.fn}()</span>
                    <span className="text-[10px] text-[var(--n-muted)]">{f.desc}</span>
                  </button>
                ))}
                <div className="px-2 pb-0.5 pt-1 text-[10px] leading-snug text-[var(--n-muted-2)]">Adds to the selected cell — type a range like A1:A5.</div>
              </div>
            )}
          </div>
          <span className="mx-1 h-4 w-px bg-[var(--n-border)]" />
          <button type="button" onClick={copyForExcel} title="Copy for Excel (TSV with formulas)" className="sheet-btn" data-testid="notes-spreadsheet-copy">
            <Copy size={12} /> {copied ? 'Copied!' : 'Copy for Excel'}
          </button>
          <button type="button" onClick={() => deleteNode?.()} title="Delete spreadsheet" className="sheet-btn sheet-btn-danger ml-auto"><Trash2 size={12} /></button>
        </div>
      )}
      <div className="overflow-x-auto n-scroll rounded-lg border border-[var(--n-border)]" contentEditable={false}>
        <table className="notes-sheet-table">
          <thead>
            <tr>
              <th className="notes-sheet-corner" />
              {Array.from({ length: cols }, (_, c) => <th key={c} className="notes-sheet-colhead">{colLabel(c)}</th>)}
            </tr>
          </thead>
          <tbody>
            {grid.map((row, r) => (
              <tr key={r}>
                <th className="notes-sheet-rowhead">{r + 1}</th>
                {row.map((cell, c) => {
                  const isActive = active.r === r && active.c === c;
                  const isEditing = editing && editing.r === r && editing.c === c;
                  return (
                    <td key={c}
                      className={`notes-sheet-cell ${isActive ? 'notes-sheet-cell-active' : ''}`}
                      style={{ background: cell.bg || undefined, textAlign: cell.a || 'left', fontWeight: cell.b ? 700 : 400 }}
                      onMouseDown={(e) => {
                        if (!editable || isEditing) return;
                        // This is a TipTap atom NodeView: without these, ProseMirror
                        // node-selects the whole spreadsheet and steals focus back,
                        // so our input blurs instantly and typing replaces the node.
                        e.preventDefault();
                        e.stopPropagation();
                        // Commit the cell currently being edited (if any) before we
                        // switch — React does not fire onBlur on unmount, so do it here.
                        if (editing && editInputRef.current) {
                          setCellValue(editing.r, editing.c, editInputRef.current.value);
                        }
                        setActive({ r, c }); setEditing({ r, c });
                      }}>
                      {isEditing && editable ? (
                        <input
                          ref={editInputRef}
                          autoFocus
                          defaultValue={cell.v}
                          className="notes-sheet-input"
                          style={{ textAlign: cell.a || 'left', fontWeight: cell.b ? 700 : 400 }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onFocus={(e) => {
                            if (fxCursorRef.current) { const p = e.target.value.lastIndexOf(')'); if (p > 0) e.target.setSelectionRange(p, p); fxCursorRef.current = false; return; }
                            if (selectOnFocusRef.current) { e.target.select(); selectOnFocusRef.current = false; }
                          }}
                          onBlur={(e) => { setCellValue(r, c, e.target.value); setEditing(null); }}
                          onKeyDown={(e) => {
                            e.stopPropagation(); // keep keys inside the cell, not ProseMirror
                            const val = e.currentTarget.value;
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              setCellValue(r, c, val);
                              const nr = e.shiftKey ? Math.max(r - 1, 0) : Math.min(r + 1, rows - 1);
                              selectOnFocusRef.current = true;
                              setActive({ r: nr, c }); setEditing({ r: nr, c });
                            } else if (e.key === 'Escape') {
                              e.preventDefault(); setEditing(null);
                            } else if (e.key === 'Tab') {
                              e.preventDefault();
                              setCellValue(r, c, val);
                              const nc = e.shiftKey ? Math.max(c - 1, 0) : Math.min(c + 1, cols - 1);
                              selectOnFocusRef.current = true;
                              setActive({ r, c: nc }); setEditing({ r, c: nc });
                            }
                          }}
                          data-testid="notes-spreadsheet-input"
                        />
                      ) : (
                        <span className="notes-sheet-value">{display[r][c]}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable && <div className="mt-1 text-[10.5px] text-[var(--n-muted-2)]" contentEditable={false}>Click a cell to edit — press <kbd>Tab</kbd> or <kbd>Enter</kbd> to move. Start with <code>=</code> for a formula, e.g. <code>=SUM(A1:A3)</code>.</div>}
    </NodeViewWrapper>
  );
}

export const Spreadsheet = Node.create({
  name: 'spreadsheet',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      data: {
        default: null,
        parseHTML: (el) => {
          try { return JSON.parse(el.getAttribute('data-sheet')); } catch { return null; }
        },
        renderHTML: (attrs) => ({ 'data-sheet': JSON.stringify(attrs.data || makeGrid(DEFAULT_ROWS, DEFAULT_COLS)) }),
      },
    };
  },

  parseHTML() { return [{ tag: 'div[data-type="spreadsheet"]' }]; },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'spreadsheet' })];
  },

  addNodeView() { return ReactNodeViewRenderer(SpreadsheetView); },

  addProseMirrorPlugins() {
    const type = this.type;
    return [
      new Plugin({
        key: new PluginKey('spreadsheetPaste'),
        props: {
          // Paste a copied Excel / Google Sheets range → drop it in as a
          // live mini-spreadsheet automatically.
          handlePaste: (view, event) => {
            const cb = event.clipboardData;
            if (!cb) return false;
            const html = cb.getData('text/html');
            const text = cb.getData('text/plain');
            let grid = null;
            if (html && /<table[\s>]/i.test(html)) grid = parseHtmlTable(html);
            if (!grid && text) grid = parseTsv(text);
            if (!grid) return false;
            const rows = grid.length;
            const cols = (grid[0] || []).length;
            if (rows < 1 || cols < 1 || (rows === 1 && cols === 1)) return false; // not tabular
            const node = type.create({ data: grid });
            const tr = view.state.tr.replaceSelectionWith(node).scrollIntoView();
            view.dispatch(tr);
            event.preventDefault();
            return true;
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      insertSpreadsheet: (rows = DEFAULT_ROWS, cols = DEFAULT_COLS) => ({ commands }) => (
        commands.insertContent({ type: this.name, attrs: { data: makeGrid(rows, cols) } })
      ),
      insertSpreadsheetData: (grid) => ({ commands }) => (
        commands.insertContent({ type: this.name, attrs: { data: grid } })
      ),
    };
  },
});

export default Spreadsheet;
